import {createRef, useContext, useEffect, useRef, useState} from "react";
import VolumeMeter from "../helper/VolumeMeter";
import Visualization from "./general/Visualization";
import MicLevel from "./general/MicLevel";
import SpeakerLevel from "./general/SpeakerLevel";
import ChatControlButton from "./general/controls/ChatControlButton";
import SVGToggleIcon from "./icons/SVGToggleIcon";
import SVGIcon from "./icons/SVGIcon";
import {useTranslation} from "react-i18next";
import VideoStream from "./stream/VideoStream";
import NotificationContext from "../context/NotificationContext";
import MediaContext, {micTestLabelKey, micTestStatusKey} from "../context/MediaContext";
import dropdownChevron from "./icons/svg/dropdown-chevron.svg";

export default (props: { hideVideo?: boolean, hideVideoControls?: boolean } = { hideVideo: false }) => {
  const {t} = useTranslation()

  const {
    cameras, setCameras,
    microphones, setMicrophones,
    speakers,
    selectedCamera, setSelectedCamera,
    selectedMicrophone, setSelectedMicrophone,
    selectedSpeaker, setSelectedSpeaker,
    selectedBackground, setSelectedBackground,
    videoEnabled, setVideoEnabled,
    audioEnabled, setAudioEnabled,
    setPermissions,
    // Defined on MediaContext, never re-derived locally — that is how the two rooms drifted apart
    // in the first place (RISKS.md/P2.28).
    cameraUnavailable, micUnavailable, micOsMuted, speakerSelectionUnavailable, speakerDeviceMissing,
    cameraWarningKey, micWarningKey,
    testSpeaker, speakerTesting,
    testMicrophone, cancelMicTest, micTestState,
    cameraStartingVisible,
    volumeMeter,
    volume, setVolume,
    stream,
    videoStream,
    loadDevices,
  } = useContext(MediaContext)

  const containerRef = createRef<HTMLDivElement>()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [cameraOpen, setCameraOpen] = useState(false)
  const [micOpen, setMicOpen] = useState(false)
  const [speakerOpen, setSpeakerOpen] = useState(false)
  const [backgroundOpen, setBackgroundOpen] = useState(false)
  const cameraRef = useRef<HTMLDivElement>(null)
  const micRef = useRef<HTMLDivElement>(null)
  const speakerRef = useRef<HTMLDivElement>(null)
  const backgroundRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (cameraRef.current && !cameraRef.current.contains(e.target as Node)) {
        setCameraOpen(false)
      }
      if (micRef.current && !micRef.current.contains(e.target as Node)) {
        setMicOpen(false)
      }
      if (speakerRef.current && !speakerRef.current.contains(e.target as Node)) {
        setSpeakerOpen(false)
      }
      if (backgroundRef.current && !backgroundRef.current.contains(e.target as Node)) {
        setBackgroundOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    // RISKS.md/P1.14: a best-effort HINT, never a gate — 'prompt' is every first visit, and
    // Safari/Firefox reject these query names outright. getUserMedia's outcome is the real signal.
    (async () => {
      await Promise.all([
        // @ts-ignore
        navigator.permissions.query({ name: "camera" }),
        // @ts-ignore
        navigator.permissions.query({ name: "microphone" })
      ]).then(([ camera, microphone ]) => {
        setPermissions({
          camera: camera.state !== 'denied',
          microphone: microphone.state !== 'denied',
        })
      }).catch(() => {
        setPermissions({ camera: true, microphone: true })
      })
    })()

    setTimeout(loadDevices, 0)
    const intervalLoadDevices = setInterval(loadDevices, 5000)

    return () => {
      clearInterval(intervalLoadDevices)
    }
  }, []);

  // `cancelMicTest`, not `testMicrophone` — that finishes the take and plays it (RISKS.md/P2.55).
  useEffect(() => {
    if (!settingsOpen) cancelMicTest()
  }, [settingsOpen, cancelMicTest])

  const setCamera = (e: any) => {
    setSelectedCamera(e.target.value)
  }

  const setMicrophone = (e: any) => {
    setSelectedMicrophone(e.target.value)
  }

  return (
    <div className="media-device-selector" ref={containerRef}>
      <div className="preview-video-wrapper">
      <div className="preview-video-container">
        {/* RISKS.md/P1.14: keyed on `cameraUnavailable`, NOT on an empty device list — a blocked
            camera is still listed, just with its id stripped. */}
        {(cameraUnavailable || !videoEnabled) && !cameraStartingVisible && (
          <span className="no-camera">
            {t(cameraUnavailable ? 'lobby.noCamera' : 'lobby.cameraOff')}
          </span>
        )}
        {/* RISKS.md/P2.38: do NOT gate this on `!cameraUnavailable` — `cameraFailed` stays true for
            the whole re-capture, so the label would show only in the last frames before the video. */}
        {videoEnabled && cameraStartingVisible && (
          <span className="no-camera">{t('lobby.cameraStarting')}</span>
        )}
        {/* RISKS.md/P2.38: a <video> keeps its last decoded frame, so it must be hidden outright when
            the stream is empty. Read at RENDER time on purpose — `videoEnabled` flips during render
            and capture only starts in the effect after, so anything slower exposes that frame. */}
        { !props.hideVideo ? (
          <VideoStream stream={videoStream} className={videoStream.getVideoTracks().length === 0 ? 'no-picture' : undefined}/>
        ) : null }

        {/* The row idles at opacity .3 — without this class the badge is invisible (RISKS.md/P2.26). */}
        { !props.hideVideoControls ? (
          <ul className={`buttons${cameraUnavailable || micUnavailable || micOsMuted ? ' buttons--warning' : ''}`}>
            <li>
              <ChatControlButton
                onClick={() => setVideoEnabled(!videoEnabled)}
                title={t('lobby.button.video.enable')}
                activeTitle={t('lobby.button.video.disable')}
                hasWarning={cameraUnavailable}
                warningTooltip={t(cameraWarningKey)}
                blocked={cameraUnavailable}
                active={videoEnabled}
                icon={new SVGToggleIcon('video-off', 'video')}/>
            </li>
            <li>
              <ChatControlButton
                onClick={() => setAudioEnabled(!audioEnabled)}
                title={t('lobby.button.microphone.enable')}
                activeTitle={t('lobby.button.microphone.disable')}
                hasWarning={micUnavailable || micOsMuted}
                warningTooltip={t(micWarningKey)}
                // NOT `|| micOsMuted` like the line above: an OS-muted microphone still works, so
                // the user must keep their own mute (RISKS.md/P2.41).
                blocked={micUnavailable}
                active={audioEnabled}
                icon={new SVGToggleIcon('microphone-off', 'microphone')}/>
            </li>
            <li>
              {/* Never disable this: with both devices blocked, speaker and background are still
                  usable (RISKS.md/P2.26). */}
              <ChatControlButton
                onClick={() => setSettingsOpen(o => !o)}
                title={t('lobby.button.settings')}
                active={settingsOpen}
                icon={new SVGIcon('settings')}/>
            </li>
          </ul>
        ) : null }
      </div>

      {settingsOpen && (
        <div className="settings-panel">
          <div className="settings-frame">
            <div className="settings-row">
              {/* RISKS.md/P2.26: always rendered — hiding it behind `permissions.camera` made the
                  whole field vanish. "None" keys on `cameraUnavailable`, not on list length: a
                  blocked camera is still enumerated, so a length check leaves blank rows. */}
              <div className="settings-col">
                  <div className="text-with-info">
                    <span className="field-label">{t('lobby.camera')}</span>
                  </div>
                  <div
                    className={`dropdown-input${cameraOpen ? ' dropdown-input--open' : ''}${cameraUnavailable ? ' dropdown-input--disabled' : ''}`}
                    ref={cameraRef}
                    onClick={() => !cameraUnavailable && setCameraOpen(o => !o)}
                  >
                    <span className="dropdown-value">
                      {cameraUnavailable ? t('lobby.none') : (cameras.find(c => c.deviceId === selectedCamera)?.label || t('conferenceInfo.selectPlaceholder'))}
                    </span>
                    <img src={dropdownChevron} alt="" aria-hidden="true" className="dropdown-chevron"/>
                    {cameraOpen && (
                      <div className="dropdown-options">
                        {cameras.map(camera => (
                          <div
                            key={camera.deviceId}
                            className={`dropdown-option${selectedCamera === camera.deviceId ? ' dropdown-option--selected' : ''}`}
                            onClick={e => { e.stopPropagation(); setSelectedCamera(camera.deviceId); setCameraOpen(false) }}
                          >
                            {camera.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
              </div>
              <div className="settings-col">
                  <div className="text-with-info">
                    <span className="field-label">{t('lobby.background')}</span>
                  </div>
                  {/* RISKS.md/P2.28: not gated on the camera — a persisted preference, not a device. */}
                  <div
                    className={`dropdown-input${backgroundOpen ? ' dropdown-input--open' : ''}`}
                    ref={backgroundRef}
                    onClick={() => setBackgroundOpen(o => !o)}
                  >
                    <span className="dropdown-value">
                      {t(`lobby.backgroundOptions.${selectedBackground}`)}
                    </span>
                    <img src={dropdownChevron} alt="" aria-hidden="true" className="dropdown-chevron"/>
                    {backgroundOpen && (
                      <div className="dropdown-options">
                        {(['none', 'blur', 'hide'] as const).map(option => (
                          <div
                            key={option}
                            className={`dropdown-option${selectedBackground === option ? ' dropdown-option--selected' : ''}`}
                            onClick={e => { e.stopPropagation(); setSelectedBackground(option); setBackgroundOpen(false) }}
                          >
                            {t(`lobby.backgroundOptions.${option}`)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
              </div>
            </div>
            <div className="settings-row">
              <div className="settings-col">
                <div className="text-with-info">
                  <span className="field-label">{t('lobby.speaker')}</span>
                  {/* The DEVICE predicate, never `speakerSelectionUnavailable`: being unable to
                      choose an output is no reason to be unable to hear one (RISKS.md/P2.26).
                      Stays live mid-tone, or a device vanishing during a test would take away the
                      restart (RISKS.md/P2.39). */}
                  <div className="test-controls">
                    {speakerTesting && <span className="test-status">{t('lobby.speakerTesting')}</span>}
                    <button
                      type="button"
                      className="device-test-btn"
                      onClick={testSpeaker}
                      disabled={speakerDeviceMissing && !speakerTesting}
                    >
                      {t('lobby.speakerTest')}
                    </button>
                  </div>
                </div>
                <div
                  className={`dropdown-input${speakerOpen ? ' dropdown-input--open' : ''}${speakerSelectionUnavailable ? ' dropdown-input--disabled' : ''}`}
                  ref={speakerRef}
                  onClick={() => !speakerSelectionUnavailable && setSpeakerOpen(o => !o)}
                >
                  <span className="dropdown-value">
                    {/* Order matters: `selectedSpeaker === null` means the SYSTEM DEFAULT, so only a
                        missing device may read as none. Never the select placeholder — output is
                        always either a device the user picked or one the system did
                        (RISKS.md/P2.26, P2.50). */}
                    {speakerDeviceMissing
                      ? t('lobby.none')
                      : (speakerSelectionUnavailable || !selectedSpeaker
                        ? t('lobby.systemDefault')
                        : (speakers.find(s => s.deviceId === selectedSpeaker)?.label || t('lobby.systemDefault')))}
                  </span>
                  <img src={dropdownChevron} alt="" aria-hidden="true" className="dropdown-chevron"/>
                  {speakerOpen && (
                    <div className="dropdown-options">
                      {speakers.map(speaker => (
                        <div
                          key={speaker.deviceId}
                          className={`dropdown-option${selectedSpeaker === speaker.deviceId ? ' dropdown-option--selected' : ''}`}
                          onClick={e => { e.stopPropagation(); setSelectedSpeaker(speaker.deviceId); setSpeakerOpen(false) }}
                        >
                          {speaker.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* NOT `speakerSelectionUnavailable`: a microphone block strips output ids, so the
                    choice goes while the sound keeps playing (RISKS.md/P2.26, P2.54). */}
                {!speakerDeviceMissing && <SpeakerLevel/>}
              </div>
              <div className="settings-col">
                <div className="text-with-info">
                  <span className="field-label">{t('lobby.microphone')}</span>
                  <div className="test-controls">
                    {micTestStatusKey(micTestState) && (
                      <span className="test-status">{t(micTestStatusKey(micTestState) as string)}</span>
                    )}
                    <button
                      type="button"
                      className="device-test-btn"
                      onClick={testMicrophone}
                      disabled={micUnavailable && micTestState === 'idle'}
                    >
                      {t(micTestLabelKey(micTestState))}
                    </button>
                  </div>
                </div>
                <div
                  className={`dropdown-input${micOpen ? ' dropdown-input--open' : ''}${micUnavailable ? ' dropdown-input--disabled' : ''}`}
                  ref={micRef}
                  onClick={() => !micUnavailable && setMicOpen(o => !o)}
                >
                  <span className="dropdown-value">
                    {micUnavailable ? t('lobby.none') : (microphones.find(m => m.deviceId === selectedMicrophone)?.label || t('conferenceInfo.selectPlaceholder'))}
                  </span>
                  <img src={dropdownChevron} alt="" aria-hidden="true" className="dropdown-chevron"/>
                  {micOpen && (
                    <div className="dropdown-options">
                      {microphones.map(mic => (
                        <div
                          key={mic.deviceId}
                          className={`dropdown-option${selectedMicrophone === mic.deviceId ? ' dropdown-option--selected' : ''}`}
                          onClick={e => { e.stopPropagation(); setSelectedMicrophone(mic.deviceId); setMicOpen(false) }}
                        >
                          {mic.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Do not re-add an `audioEnabled` gate: self-mute must look like an OS mute (RISKS.md/P2.51). */}
                {!micUnavailable && <MicLevel/>}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}